import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button } from '@clay-run/glaze/button';
import { Heading } from '@clay-run/glaze/heading';
import { SwitchField } from '@clay-run/glaze/switch-field';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@clay-run/glaze/table';
import { Text } from '@clay-run/glaze/text';
import { useAppBasePath } from '../../_shared/base-path';
import { pct, usd } from '../format';
import { campaignTotals, findCampaign, type CampaignPerformance } from '../performance-fixtures';
import { CampaignStatusBadge } from './CampaignStatusBadge';
import { CplChart } from './CplChart';
import { Kpi, Panel } from './Panels';

function CampaignDetail({ campaign: c }: { campaign: CampaignPerformance }) {
  const base = useAppBasePath();
  const t = campaignTotals(c);
  const isEnded = c.status === 'ended';
  const [selfHealing, setSelfHealing] = useState(c.selfHealing);
  const [proposalOpen, setProposalOpen] = useState(c.proposal !== null);

  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-wrap items-center gap-3">
        <div className="flex flex-col gap-1">
          <Link to={`${base}/performance`} className="text-xs text-content-tertiary hover:underline">
            Performance
          </Link>
          <div className="flex flex-wrap items-center gap-3">
            <Heading level={1} size="lg">
              {c.name}
            </Heading>
            <CampaignStatusBadge campaign={c} />
            <Text size="sm" variant="tertiary">
              {c.team}
            </Text>
          </div>
        </div>
        {!isEnded && (
          <div className="ml-auto flex gap-2">
            <Button variant="secondary">Edit</Button>
            <Button variant="secondary">{c.status === 'paused' ? 'Resume' : 'Pause'}</Button>
            <Button variant="danger-secondary">End campaign</Button>
          </div>
        )}
      </header>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-5">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            <Kpi label="Spend" value={usd(t.spendUsd)} sub={`of ${usd(c.plannedSpendUsd)} planned`} />
            <Kpi label="Leads" value={String(t.leads)} sub={c.platforms.map(p => `${p.platform} ${p.leads}`).join(' · ')} />
            <Kpi
              label="CPL"
              value={usd(t.cplUsd, 2)}
              sub={`Decision metric · target ${usd(c.cplTargetUsd)}`}
              alert={t.overTarget}
            />
            <Kpi
              label="Qualified"
              value={pct(t.qualifiedRate)}
              sub={`Guardrail · ${pct(c.qualifiedRateFloor)} floor`}
              alert={t.belowFloor}
            />
            <Kpi label="CPQL" value={usd(t.cpqlUsd, 2)} sub={`${t.qualifiedLeads} qualified leads`} />
          </div>

          <Panel className="flex flex-col gap-3">
            <div className="flex flex-wrap items-baseline gap-3">
              <Heading level={2} size="sm">
                {c.chart.platform} daily CPL
              </Heading>
              <Text size="sm" variant="tertiary">
                Target {usd(c.cplTargetUsd)} ·{' '}
                {t.daysOverTarget > 0 ? `above target ${t.daysOverTarget} days running` : 'at or under target on the latest day'}
              </Text>
            </div>
            <CplChart platform={c.chart.platform} dailyCpl={c.chart.dailyCplUsd} target={c.cplTargetUsd} />
          </Panel>

          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Platform</TableHeader>
                <TableHeader align="right">Spend</TableHeader>
                <TableHeader align="right">Leads</TableHeader>
                <TableHeader align="right">Qualified</TableHeader>
                <TableHeader align="right">CPL</TableHeader>
                <TableHeader align="right">CPQL</TableHeader>
                <TableHeader align="right">Benchmark CPL</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {c.platforms.map(row => {
                const rowCpl = row.spendUsd / row.leads;
                return (
                  <TableRow key={row.platform}>
                    <TableCell>
                      {row.platform} · {row.format}
                    </TableCell>
                    <TableCell align="right">{usd(row.spendUsd)}</TableCell>
                    <TableCell align="right">{row.leads}</TableCell>
                    <TableCell align="right">
                      {row.qualifiedLeads} · {pct(row.qualifiedLeads / row.leads)}
                    </TableCell>
                    <TableCell align="right">
                      <span className={rowCpl > row.benchmarkCplUsd ? 'font-semibold text-text-red' : undefined}>
                        {usd(rowCpl, 2)}
                      </span>
                    </TableCell>
                    <TableCell align="right">{usd(row.spendUsd / row.qualifiedLeads, 2)}</TableCell>
                    <TableCell align="right">{usd(row.benchmarkCplUsd, 2)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        <aside className="flex flex-col gap-4">
          <Panel className="flex flex-col gap-3">
            <SwitchField
              label="Self-healing"
              description={
                isEnded
                  ? 'This campaign has ended.'
                  : 'Acts only when the decision metric breaches and the guardrail confirms it. Every action is logged and reversible.'
              }
              checked={selfHealing}
              onCheckedChange={setSelfHealing}
              disabled={isEnded}
            />
            <dl className="grid grid-cols-[1fr_auto] gap-x-3 gap-y-2 text-sm">
              <dt>Decision metric</dt>
              <dd className="font-medium">CPL &gt; {usd(c.cplTargetUsd)} for 3 days</dd>
              <dt>Guardrail</dt>
              <dd className="font-medium">Qualified &lt; {pct(c.qualifiedRateFloor)}</dd>
              <dt>Minimum data</dt>
              <dd className="font-medium">
                {c.minLeadsBeforeAction} leads · {t.leads >= c.minLeadsBeforeAction ? 'met' : `${t.leads} so far`}
              </dd>
              <dt>Step size</dt>
              <dd className="font-medium">−{c.budgetStepPercent}% budget</dd>
            </dl>
          </Panel>

          {c.proposal && selfHealing && proposalOpen && (
            <div className="flex flex-col gap-2.5 rounded-2xl bg-bg-orange-light p-5">
              <Text weight="semibold">{c.proposal.title}</Text>
              <Text size="sm">{c.proposal.detail}</Text>
              <div className="flex gap-2">
                <Button variant="primary" onClick={() => setProposalOpen(false)}>
                  Approve
                </Button>
                <Button variant="secondary" onClick={() => setProposalOpen(false)}>
                  Dismiss
                </Button>
              </div>
            </div>
          )}

          <Panel className="flex flex-col">
            <span className="pb-2 font-mono text-[11px] uppercase tracking-[0.12em] text-content-tertiary">Action log</span>
            {c.actionLog.map(entry => (
              <div key={entry.id} className="flex gap-3 border-t border-border-secondary py-2 text-sm">
                <span className="flex-1">{entry.text}</span>
                <span className="whitespace-nowrap text-content-tertiary">{entry.when}</span>
              </div>
            ))}
          </Panel>

        </aside>
      </div>
    </section>
  );
}

export default function CampaignDetailPage() {
  const { campaignId = '' } = useParams<{ campaignId: string }>();
  const base = useAppBasePath();
  const campaign = findCampaign(campaignId);

  if (!campaign) {
    return (
      <div className="flex flex-col gap-2">
        <Text variant="tertiary">This campaign couldn't be found.</Text>
        <Link to={`${base}/performance`} className="text-sm hover:underline">
          Back to performance
        </Link>
      </div>
    );
  }

  return <CampaignDetail key={campaign.id} campaign={campaign} />;
}
